import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IEmployeeAuthService } from './i-employee-auth.service';
import { IonApiClientService } from 'shared';
import { Employee } from '../../app/models/employee';
import { IEmployeeLoginCredentials } from '../../app/models/iemployeelogin-credentials';
import moment from 'moment';
import { AppEmployeePolicyRoles } from 'pm-src/app/app.employee-policies.config';
import { IEmployeePolicyRoles } from 'pm-src/app/types/i-employee-policy-roles';

@Injectable({
    providedIn: 'root',
})
export class EmployeeAuthService implements IEmployeeAuthService {
    private _employeeSubject: BehaviorSubject<Employee>;
    public employee$: Observable<Employee>;

    private _employeeStorageName: string;
    private _employeePolicyRoles = AppEmployeePolicyRoles;

    public get currentEmployee(): Employee {
        return this._employeeSubject.getValue();
    }

    constructor(private _ionApiClientService: IonApiClientService) {
        this._employeeStorageName = 'Employee';
        this._employeeSubject = new BehaviorSubject<Employee>(
            this.getEmployeeFromStorage()
        );
        this.employee$ = this._employeeSubject.asObservable();
    }

    public login(
        loginCredentials: IEmployeeLoginCredentials
    ): Observable<HttpResponse<Employee>> {
        return this._ionApiClientService
            .post<Employee>('provideremployee/login', loginCredentials)
            .pipe(
                map((response) => {
                    if (response.ok && response.body) {
                        const currentEmployee = new Employee(this._employeePolicyRoles);
                        Object.assign(currentEmployee, response.body);
                        this._employeeSubject.next(currentEmployee);
                        const expiryDataString = JSON.stringify({
                            employee: currentEmployee,
                            expiry: moment().add(3, 'hours'),
                        });
                        localStorage.setItem(this._employeeStorageName, expiryDataString);
                    }
                    return response;
                })
            );
    }

    public logout(): Observable<HttpResponse<void>> {
        return this._ionApiClientService.post<void>(`provideremployee/logout`, null).pipe(
            map((response) => {
                if (response.status === 200) {
                    this.removeCurrentEmployee();
                }
                return response;
            })
        );
    }

    public hasActiveEmployee(): boolean {
        const storedEmployee = localStorage.getItem(this._employeeStorageName);
        if (!storedEmployee) {
            this._employeeSubject.next(null);
            return false;
        }

        const expiryData = JSON.parse(storedEmployee);
        if (expiryData.expiry && !moment(expiryData.expiry).isAfter(moment())) {
            this.removeCurrentEmployee();
            return false;
        }

        return true;
    }

    public getPolicyRoles(): IEmployeePolicyRoles[] {
        return this._employeePolicyRoles;
    }

    public removeCurrentEmployee(): void {
        localStorage.removeItem(this._employeeStorageName);
        this._employeeSubject.next(null);
    }

    public getEmployeeFromStorage(): Employee {
        const employeeData = JSON.parse(localStorage.getItem(this._employeeStorageName));
        if (employeeData === null) {
            return null;
        }
        return employeeData.employee;
    }
}
